import { useCallback, useEffect, useRef, useState } from "react";
import type { MutableRefObject, ReactNode } from "react";

import { CSSTransition } from "react-transition-group";

import { onboardingAgentExternalService } from "../onboardingAgentExternalService";
import { useOnboardingAgentNudge } from "../OnboardingAgentNudge/hooks/useOnboardingAgentNudge";
import { OnboardingAgentNudge } from "../OnboardingAgentNudge/OnboardingAgentNudge";
import { useOnboardingAgentPanel } from "../OnboardingAgentPanel/hooks/useOnboardingAgentPanel";
import { OnboardingAgentPanel } from "../OnboardingAgentPanel/OnboardingAgentPanel";
import type { UseOnboardingAgentPanelResult } from "../OnboardingAgentPanel/OnboardingAgentPanel.types";
import { onboardingAgentTrackingService } from "../onboardingAgentTrackingService";
import { OnboardingAgentTrigger } from "../OnboardingAgentTrigger/OnboardingAgentTrigger";

import { PANEL_TRANSITION_MS } from "./constants";
import type { OnboardingAgentPanelPlacement } from "./helpers/OnboardingAgentPosition.helpers";
import { useOnboardingAgent } from "./hooks/useOnboardingAgent";
import { AnimatedPanelMount } from "./styles";

type PanelMountProps = {
    readonly isPanelOpen: boolean;
    readonly isFirstOpen: boolean;
    readonly isPanelExpanded: boolean;
    readonly shouldAnimatePanelPosition: boolean;
    readonly chat: UseOnboardingAgentPanelResult;
    readonly panelMountRef: MutableRefObject<HTMLDivElement | null>;
    readonly panelPlacement: OnboardingAgentPanelPlacement;
    readonly onToggleExpand: () => void;
    readonly onPanelExited: () => void;
};

const PanelMount = ({
    isPanelOpen,
    isFirstOpen,
    isPanelExpanded,
    shouldAnimatePanelPosition,
    chat,
    panelMountRef,
    panelPlacement,
    onToggleExpand,
    onPanelExited,
}: PanelMountProps): ReactNode => (
    <CSSTransition
        nodeRef={panelMountRef}
        in={isPanelOpen}
        timeout={PANEL_TRANSITION_MS}
        classNames="onboarding-agent-panel"
        onExited={onPanelExited}
        mountOnEnter
        unmountOnExit
    >
        <AnimatedPanelMount
            ref={panelMountRef}
            $x={panelPlacement.x}
            $y={panelPlacement.y}
            $placement={panelPlacement.placement}
            $shouldAnimatePosition={shouldAnimatePanelPosition}
            data-automation="onboarding-agent-panel-root"
        >
            <OnboardingAgentPanel
                chat={chat}
                isFirstOpen={isFirstOpen}
                isExpanded={isPanelExpanded}
                onToggleExpand={onToggleExpand}
            />
        </AnimatedPanelMount>
    </CSSTransition>
);

export const OnboardingAgent = () => {
    const threadIdRef = useRef<string | undefined>(undefined);
    const getThreadId = useCallback((): string | undefined => threadIdRef.current, []);
    const [pendingMessage, setPendingMessage] = useState<string | null>(null);

    const {
        isPanelOpen,
        isFirstOpen,
        isPanelExpanded,
        shouldAnimatePanelPosition,
        shouldAnimateTriggerPosition,
        panelMountRef,
        triggerPosition,
        panelPlacement,
        handleOpenPanel,
        handleClosePanel,
        handlePanelExited,
        handleToggleExpand,
        handleTriggerDragMove,
        handleTriggerDragEnd,
    } = useOnboardingAgent({ getThreadId });
    const chat = useOnboardingAgentPanel({ onClose: handleClosePanel, isPanelOpen });

    useEffect(() => {
        threadIdRef.current = chat.threadId ?? undefined;
    }, [chat.threadId]);

    const chatRef = useRef(chat);
    chatRef.current = chat;

    const askFromNudge = useCallback(
        (question: string): void => {
            handleOpenPanel();
            setPendingMessage(question);
        },
        [handleOpenPanel],
    );

    const nudge = useOnboardingAgentNudge({ isPanelOpen, onAsk: askFromNudge });

    useEffect(() => {
        onboardingAgentTrackingService.trackAgentExist();
    }, []);

    useEffect(() => {
        onboardingAgentExternalService.register((message?: string) => {
            handleOpenPanel();
            if (message?.trim()) {
                setPendingMessage(message);
            }
        });

        return () => onboardingAgentExternalService.unregister();
    }, [handleOpenPanel]);

    useEffect(() => {
        if (isPanelOpen && pendingMessage) {
            chatRef.current.handleSend(pendingMessage);
            setPendingMessage(null);
        }
    }, [isPanelOpen, pendingMessage]);

    return (
        <>
            <OnboardingAgentTrigger
                position={triggerPosition}
                shouldAnimatePosition={shouldAnimateTriggerPosition}
                isPanelOpen={isPanelOpen || nudge.isVisible}
                onOpen={handleOpenPanel}
                onDragMove={handleTriggerDragMove}
                onDragEnd={handleTriggerDragEnd}
            />
            <OnboardingAgentNudge
                position={triggerPosition}
                isVisible={nudge.isVisible}
                suggestions={nudge.suggestions}
                onAsk={nudge.handleAsk}
                onDismiss={nudge.handleDismiss}
            />
            <PanelMount
                isPanelOpen={isPanelOpen}
                isFirstOpen={isFirstOpen}
                isPanelExpanded={isPanelExpanded}
                shouldAnimatePanelPosition={shouldAnimatePanelPosition}
                chat={chat}
                panelMountRef={panelMountRef}
                panelPlacement={panelPlacement}
                onToggleExpand={handleToggleExpand}
                onPanelExited={handlePanelExited}
            />
        </>
    );
};
